import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { CartErrorEvent } from '@shopify/events';

/**
 * ECS: saves the shopper's gift message to a cart attribute as they type, so it
 * reaches the order without depending on the checkout button submitting the
 * cart form. Modelled on the native cart note (cart-note.js).
 *
 * The attribute key comes from `data-attribute-key` so it is defined once, in
 * the Liquid that renders the field.
 */
class CartGiftNote extends Component {
  /**
   * Saves run one after another so an older value can never land after a newer
   * one. (Aborting an in-flight fetch only cancels it client-side; the server may
   * still apply it after the request that replaced it.)
   * @type {Promise<void>}
   */
  #saveQueue = Promise.resolve();

  /** @type {string | null} */
  #queuedValue = null;

  /**
   * Handles updates to the gift message.
   * @param {InputEvent} event - The input event in our text-area.
   */
  updateGiftNote = debounce((event) => {
    if (!(event.target instanceof HTMLTextAreaElement)) return;

    // Only the latest value matters: a save that is still waiting its turn
    // picks up whatever was typed most recently.
    const alreadyQueued = this.#queuedValue !== null;
    this.#queuedValue = event.target.value;
    if (alreadyQueued) return;

    this.#saveQueue = this.#saveQueue.then(() => this.#save());
  }, 200);

  async #save() {
    const key = this.dataset.attributeKey;
    const value = this.#queuedValue;
    this.#queuedValue = null;
    if (!key || value === null) return;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ attributes: { [key]: value } }),
      });

      const response = await fetch(Theme.routes.cart_update_url, config);

      if (!response.ok) throw new Error(`Failed to update gift message (${response.status})`);
    } catch (error) {
      this.dispatchEvent(
        new CartErrorEvent({
          error: (error instanceof Error && error.message) || 'Failed to update gift message',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    }
  }
}

if (!customElements.get('cart-gift-note')) {
  customElements.define('cart-gift-note', CartGiftNote);
}
